import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createTransform, createMigrate, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import AsyncStorage from '@react-native-async-storage/async-storage';
import localforage from 'localforage';
import { Platform } from 'react-native';
import authReducer from './authSlice';
import inventoryReducer from './inventorySlice';
import languageReducer from './languageSlice';
import mealPlanReducer from './mealPlanSlice';
import groceryListReducer from './groceryListSlice';
import userPreferencesReducer from './userPreferencesSlice';
import recipeMetadataReducer from './recipeMetadataSlice';
import { persistMigrations, PERSIST_VERSION } from './persistMigrations';
import dealsReducer from './dealsSlice';
import navigationReducer from './navigationSlice';
import feedbackReducer from './feedbackSlice';
import onboardingReducer from './onboardingSlice';
import announcementsReducer from './announcementsSlice';
import recipeIndexReducer from './recipeIndexSlice';
import pantrySyncReducer from './pantrySyncSlice';
import pantryLearningReducer from './pantryLearningSlice';
import cookingSessionReducer from './cookingSessionSlice';
import mealPlanUIReducer from './mealPlanUISlice';
import cookLogReducer from './cookLogSlice';
import uiReducer from './uiSlice';
import { mealPlanSyncMiddleware, flushPendingMealPlanSync } from './mealPlanSyncMiddleware';
import { groceryListSyncMiddleware } from './groceryListSyncMiddleware';
import {
  recipeMetadataSyncMiddleware,
  flushPendingRecipeMetadataSync,
  registerPersistFlush,
} from './recipeMetadataSyncMiddleware';
import { cookLogSyncMiddleware, flushPendingCookLogSync } from './cookLogSyncMiddleware';
import { cookNoteJournalMiddleware } from './cookNoteJournal';
import { userPreferencesSyncMiddleware } from './userPreferencesSyncMiddleware';
import { languageSyncMiddleware } from './languageSyncMiddleware';
import { feedbackSyncMiddleware } from './feedbackSyncMiddleware';
import { analyticsMiddleware } from './analyticsMiddleware';
import { onboardingPersistMiddleware, registerOnboardingPersistFlush } from './onboardingPersistMiddleware';
import {
  createDeviceStorageBudget,
  estimateDeviceStorage,
  registerLowStorageHandler,
  shedViaRegisteredHandler,
  type QuotaExceededReport,
} from '../storage/DeviceStorageBudget';
import { shedForStorage, storageBudgetTransform, withStorageShedding } from './storageShedding';
import { captureError, trackEvent } from '../services/analyticsService';
import { withLocation } from '../utils/pantryLocation';
import { markStartup } from '../utils/startupPerf';
import {
  createNoopStorage,
  makeMigratingStorage,
  mealPlanTransform,
  recipeIndexTransform,
} from './persistStorage';
import { mirrorHouseholdContext } from './householdContextMirror';

// Web storage for redux-persist. A write that does not fit walks the quota
// ladder in DeviceStorageBudget — shedding cloud-mirrored state one tier at a
// time and reporting each rung — instead of offering to delete everything.
const createWebStorage = () => {
  const hasLocalStorage = typeof window !== 'undefined' && Boolean(window.localStorage);
  const budget = hasLocalStorage
    ? createDeviceStorageBudget(window.localStorage, {
        shed: shedViaRegisteredHandler,
        report: reportQuotaExceeded,
      })
    : null;
  return {
    setItem: (key: string, value: string) => (budget ? budget.write(key, value) : Promise.resolve()),
    getItem: (key: string) =>
      Promise.resolve(hasLocalStorage ? window.localStorage.getItem(key) : null),
    removeItem: (key: string) => {
      if (hasLocalStorage) window.localStorage.removeItem(key);
      return Promise.resolve();
    },
  };
};

/**
 * Every rung of the ladder is an event, because until now a full box was a
 * `console.warn` and PostHog had zero of them in 60 days. A rung that did not
 * get the state onto disk is also an error: that is data sitting in memory
 * with nowhere to go.
 */
const reportQuotaExceeded = (report: QuotaExceededReport): void => {
  void estimateDeviceStorage().then(estimate => {
    const properties = {
      key: report.key,
      rung: report.rung,
      recovered: report.recovered,
      write_bytes: report.writeBytes,
      local_storage_bytes: report.totalBytes,
      largest_keys: Object.fromEntries(report.largestKeys),
      origin_usage_bytes: estimate?.usage ?? null,
      origin_quota_bytes: estimate?.quota ?? null,
      error: report.error,
    };
    trackEvent('storage_quota_exceeded', properties);
    if (!report.recovered) {
      captureError(new Error(`Device storage exhausted at rung ${report.rung}: ${report.error}`), properties);
    }
  });
};

// Storage engine for the large caches (mealPlan, recipeIndex).
// Web: IndexedDB via localforage (hundreds of MB, async, off the ~5 MB
// localStorage quota). Native: AsyncStorage (SQLite, not 5 MB-capped).
const idbBase =
  Platform.OS === 'web'
    ? (typeof window !== 'undefined' && window.indexedDB
        ? localforage.createInstance({ name: 'paprika', storeName: 'cache' })
        : createNoopStorage())
    : AsyncStorage;

// Lazy one-time migration: existing users have mealPlan/recipeIndex inside the
// localStorage `persist:root` blob. On the first read of a moved key, fall back
// to that legacy copy and write it through to the new engine.
const idbStorage = makeMigratingStorage(idbBase, () =>
  typeof window !== 'undefined' && window.localStorage
    ? window.localStorage.getItem('persist:root')
    : null
);

// Transform to exclude large image data from persistence
const inventoryTransform = createTransform(
  // Transform state before persisting (inbound)
  (inboundState: any, key) => {
    if (inboundState?.items) {
      // Remove imageUri from items to save storage space
      const optimizedItems = inboundState.items.map((item: any) => {
        const { imageUri, ...itemWithoutImage } = item;
        return itemWithoutImage;
      });
      return {
        ...inboundState,
        items: optimizedItems
      };
    }
    return inboundState;
  },
  // Transform state when rehydrating (outbound)
  (outboundState: any, key) => {
    // Migrate items to have status field and location (backwards compatibility)
    if (outboundState?.items) {
      const migratedItems = outboundState.items.map((item: any) => {
        let migrated = item;
        if (!migrated.status) migrated = { ...migrated, status: 'active' };
        if (!migrated.location && migrated.category !== undefined) {
          migrated = withLocation(migrated);
        }
        return migrated;
      });
      return {
        ...outboundState,
        items: migratedItems
      };
    }
    return outboundState;
  },
  // Only apply to inventory slice
  { whitelist: ['inventory'] }
);

// How long redux-persist may batch writes before hitting storage. Pending
// writes are flushed explicitly when the tab is hidden, so the exposure window
// is a background crash rather than a normal close.
const PERSIST_THROTTLE_MS = 1000;

// Large caches live in IndexedDB (web) / AsyncStorage (native) via their own
// nested persist, off the ~5 MB synchronous localStorage quota shared by root.
const mealPlanPersistConfig = {
  key: 'mealPlan',
  storage: idbStorage,
  transforms: [mealPlanTransform],
  // Coalesce writes: unthrottled, every action re-serialized this slice and hit
  // storage, which on native means a JSON pass plus an AsyncStorage bridge call
  // per dispatch. Pending writes are flushed on visibilitychange (see (app)/_layout).
  throttle: PERSIST_THROTTLE_MS,
  // currentWeekStart: always re-init from initialState (getTodayStart).
  // localHydrated / cloudHydrated describe *this boot*, so they must never
  // come back from disk. autoMergeLevel1 keeps the reducer's value only when
  // the reducer changed it during REHYDRATE — true for localHydrated, false
  // for cloudHydrated, which would otherwise rehydrate as `true` and tell the
  // planner the cloud had answered before a single read went out.
  blacklist: ['currentWeekStart', 'localHydrated', 'cloudHydrated'],
};

const recipeIndexPersistConfig = {
  key: 'recipeIndex',
  storage: idbStorage,
  transforms: [recipeIndexTransform],
  throttle: PERSIST_THROTTLE_MS,
};

const rootReducer = combineReducers({
  auth: authReducer,
  inventory: inventoryReducer,
  language: languageReducer,
  mealPlan: persistReducer(mealPlanPersistConfig, mealPlanReducer),
  groceryList: groceryListReducer,
  userPreferences: userPreferencesReducer,
  recipeMetadata: recipeMetadataReducer,
  deals: dealsReducer,
  navigation: navigationReducer,
  feedback: feedbackReducer,
  onboarding: onboardingReducer,
  announcements: announcementsReducer,
  recipeIndex: persistReducer(recipeIndexPersistConfig, recipeIndexReducer),
  pantrySync: pantrySyncReducer,
  pantryLearning: pantryLearningReducer,
  cookingSession: cookingSessionReducer,
  mealPlanUI: mealPlanUIReducer,
  cookLog: cookLogReducer,
  ui: uiReducer,
});

// Root persist holds only the small, latency-sensitive slices in localStorage.
// mealPlan and recipeIndex are intentionally excluded — they persist via their
// own IndexedDB-backed nested persists above.
const persistConfig = {
  key: 'root',
  version: PERSIST_VERSION,
  migrate: createMigrate(persistMigrations as any, { debug: false }),
  storage: Platform.OS === 'web' ? createWebStorage() : AsyncStorage,
  whitelist: ['auth', 'inventory', 'language', 'groceryList', 'userPreferences', 'recipeMetadata', 'deals', 'navigation', 'feedback', 'onboarding', 'announcements', 'pantrySync', 'pantryLearning', 'cookingSession', 'cookLog'],
  // storageBudgetTransform last: an evicted slice is written as nothing.
  transforms: [inventoryTransform, storageBudgetTransform],
  debug: false, // Persist's per-action logging floods the console and drowns real warnings
  throttle: PERSIST_THROTTLE_MS,
};

const persistedReducer = persistReducer(persistConfig, withStorageShedding(rootReducer as any) as any);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).prepend(
      mealPlanSyncMiddleware.middleware,
      groceryListSyncMiddleware.middleware,
      recipeMetadataSyncMiddleware.middleware,
      cookLogSyncMiddleware.middleware,
      // Copies every cook note into the recipe's journal, so it outlives the plan (#795).
      cookNoteJournalMiddleware.middleware,
      userPreferencesSyncMiddleware.middleware,
      languageSyncMiddleware.middleware,
      feedbackSyncMiddleware.middleware,
      analyticsMiddleware.middleware,
      onboardingPersistMiddleware.middleware
    ),
});

// Delay storage cleanup to run AFTER Firebase auth redirect check
// Firebase needs to check for redirect results on page load first
if (Platform.OS === 'web') {
  try {
    // Import and run cleanup AFTER a delay (dynamic import to avoid SSR issues)
    // 3 second delay gives Firebase time to check getRedirectResult()
    setTimeout(() => {
      markStartup('storageCleanup: start');
      import('./storageCleanup').then(({ checkAndFixStoredInventory }) => {
        console.log('Running delayed storage cleanup (after Firebase auth check)...');
        checkAndFixStoredInventory();
        markStartup('storageCleanup: done');
      }).catch(error => {
        console.warn('Failed to run storage cleanup:', error);
      });
    }, 3000);
  } catch (error) {
    console.warn('Storage cleanup unavailable:', error);
  }

  // Flush any debounced meal-plan writes before the tab is hidden/closed, so a
  // quick "add note → close tab" doesn't drop the change inside the 1s debounce
  // window (the sync would otherwise never reach Firestore and be wiped on the
  // next reload by the live-sync merge).
  try {
    const flushMealPlan = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        void flushPendingMealPlanSync(store.getState);
      }
    };
    document.addEventListener('visibilitychange', flushMealPlan);
    // pagehide fires on iOS Safari where visibilitychange is unreliable.
    window.addEventListener('pagehide', () => void flushPendingMealPlanSync(store.getState));
  } catch (error) {
    console.warn('Could not attach meal-plan flush listener:', error);
  }

  // The same drain for ratings and verdicts, which had no flush at all. Tapping
  // a verdict and locking the phone is a normal way to rate a meal, and it lost
  // the answer outright: the write sat behind a debounce, the page was
  // suspended before it fired, and nothing ever retried it.
  //
  // `visible` and `online` are retries, not flushes: whatever an earlier
  // failure left in the outbox goes out as soon as there is a tab and a network
  // to send it with.
  try {
    const drainMetadata = () => {
      void flushPendingRecipeMetadataSync(store.getState, store.dispatch);
      // The cook journal's outbox, which now carries every cook note (#795).
      void flushPendingCookLogSync(store.getState, store.dispatch);
    };
    document.addEventListener('visibilitychange', drainMetadata);
    window.addEventListener('pagehide', drainMetadata);
    window.addEventListener('online', drainMetadata);
  } catch (error) {
    console.warn('Could not attach recipe-metadata flush listener:', error);
  }
}

// Before persistStore, so the household restored from disk reaches
// householdContext in the same tick the store gets it.
mirrorHouseholdContext(store);

export const persistor = persistStore(store);

// A rating has to be on disk the instant it is tapped, not up to a second
// later: the outbox that makes the retry possible is persisted with the rest of
// the slice, and PERSIST_THROTTLE_MS is long enough to lose both if the tab
// goes away in between. Registered rather than imported by the middleware,
// which this file already imports.
registerPersistFlush(() => persistor.flush());
// A write that does not fit marks one tier of cloud-mirrored state as evicted
// from disk and flushes, so the trimmed blob is written inside the same call
// and the ladder can see whether it fit. The ladder calls this from a
// microtask, never from inside the failing write: an explicit persistor.flush()
// runs storage from within redux-persist's FLUSH reducer, where a dispatch
// would throw.
registerLowStorageHandler(tier => {
  store.dispatch(shedForStorage(tier));
  void persistor.flush();
});
// Onboarding steps are flushed too: the quiz lives outside the (app) layout's
// hide-flush, and its whole point is that a reload resumes where the cook was.
registerOnboardingPersistFlush(() => persistor.flush());

// Use rootReducer (not store.getState) to avoid persistReducer's Partial<> wrapper.
// All slices are always defined after initialization.
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
